import * as React from "react"
import { Calendar } from "@/components/ui/calendar"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { cn } from "@/lib/utils"
import { CalendarDays } from "lucide-react"
import { Button } from "@/components/ui/Button"

interface DateTimePickerProps {
  value?: string | null
  onChange?: (value: string | null) => void
  disabled?: boolean
  placeholder?: string
  minDate?: Date
  className?: string
}

function formatTime(hour: number, minute: number): string {
  return `${hour.toString().padStart(2, "0")}:${minute.toString().padStart(2, "0")}`
}

function parseTime(value: string): { hour: number; minute: number } {
  const [h, m] = value.split(":").map(Number)
  return { hour: h ?? 9, minute: m ?? 0 }
}

export function DateTimePicker({
  value,
  onChange,
  disabled = false,
  placeholder = "Selecionar data e horário",
  minDate,
  className,
}: DateTimePickerProps) {
  const [open, setOpen] = React.useState(false)
  const [selectedDate, setSelectedDate] = React.useState<Date | undefined>(
    value ? new Date(value) : undefined
  )
  const [selectedTime, setSelectedTime] = React.useState<string>(
    value ? formatTime(new Date(value).getHours(), new Date(value).getMinutes()) : "09:00"
  )

  React.useEffect(() => {
    if (value) {
      const date = new Date(value)
      setSelectedDate(date)
      setSelectedTime(formatTime(date.getHours(), date.getMinutes()))
    } else {
      setSelectedDate(undefined)
      setSelectedTime("09:00")
    }
  }, [value])

  const handleDateSelect = (date: Date | undefined) => {
    setSelectedDate(date)
    if (date) {
      const time = parseTime(selectedTime)
      const newDate = new Date(date)
      newDate.setHours(time.hour, time.minute, 0, 0)
      onChange?.(newDate.toISOString())
    }
  }

  const handleTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const timeValue = e.target.value
    setSelectedTime(timeValue)
    if (selectedDate) {
      const time = parseTime(timeValue)
      const newDate = new Date(selectedDate)
      newDate.setHours(time.hour, time.minute, 0, 0)
      onChange?.(newDate.toISOString())
    }
  }

  const handleClear = () => {
    setSelectedDate(undefined)
    setSelectedTime("09:00")
    onChange?.(null)
  }

  const displayValue = value
    ? new Date(value).toLocaleString("pt-BR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : ""

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          disabled={disabled}
          className={cn(
            "w-full justify-start text-left font-normal hover:bg-accent",
            !value && "text-muted-foreground",
            className
          )}
        >
          <CalendarDays className="mr-2 h-4 w-4" />
          {displayValue || placeholder}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <div className="flex flex-col sm:flex-row gap-4 p-4">
          <div>
            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={handleDateSelect}
              disabled={(date) => minDate ? date < minDate : false}
              fromDate={minDate}
            />
          </div>
          <div className="flex flex-col gap-2 border-l border-border pl-4">
            <label className="text-sm font-medium text-foreground">Horário</label>
            <input
              type="time"
              value={selectedTime}
              onChange={handleTimeChange}
              disabled={disabled || !selectedDate}
              className="flex h-9 w-full rounded-md border border-border bg-card text-foreground px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:border-accent disabled:bg-surface disabled:cursor-not-allowed disabled:opacity-50"
            />
            <div className="flex gap-2 pt-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleClear}
                disabled={disabled}
              >
                Limpar
              </Button>
              <Button
                type="button"
                size="sm"
                onClick={() => setOpen(false)}
              >
                Confirmar
              </Button>
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}